import React, { useState } from "react";
import {
  Button,
  Grid,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCustomSnackbar } from "../../components/CustomSnackbar";
import * as Yup from "yup";
import { ASSIGN_TEST_INSTANCE } from "../../graphql/mutations";
import {
  GET_TEST_INSTANCES_ASSIGNED_TO_ME,
  GET_TEST_INSTANCES_ASSIGNED_BY_ME,
} from "../../graphql/queries";
import { useMutation } from "@apollo/client";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { handleCacheUpdate } from "../../cache";
import useGuardedRoute from "../../hooks/useGuardedRoute";
import { useAppContext } from "../../context/AppContext";
import TagControl from "../../components/Tags/TagControl";

const AssignCandidateForm = () => {
  const { t } = useTranslation();
  const { lsEnqueueSnackbar } = useCustomSnackbar();
  const navigate = useNavigate();

  const { store } = useAppContext();

  useGuardedRoute("/app", [store.currentTestTemplate]);

  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [tags, setTags] = useState([]);

  function handleOpenDialog() {
    setOpenConfirmationDialog(true);
  }

  function handleCloseDialog() {
    setOpenConfirmationDialog(false);
  }

  const [assignTestInstance] = useMutation(ASSIGN_TEST_INSTANCE, {
    update: (cacheClient, response) => {
      if (response?.data) {
        handleCacheUpdate(
          "create",
          cacheClient,
          response.data.assignTestInstance,
          "id",
          GET_TEST_INSTANCES_ASSIGNED_TO_ME,
          "getTestInstancesAssignedToMe",
          null,
          {}
        );
        
        handleCacheUpdate(
          "create",
          cacheClient,
          response.data.assignTestInstance,
          "id",
          GET_TEST_INSTANCES_ASSIGNED_BY_ME,
          "getTestInstancesAssignedByMe",
          null,
          { orgId: store.currentOrg?.org.id }
        );
      }
    },
  });

  const validationSchema = Yup.object({
    firstName: Yup.string().required(t("Required")),
    lastName: Yup.string().required(t("Required")),
    email: Yup.string().email(t("MustBeValidEmail")).required(t("Required")),
    targetCompletionDate: Yup.string().required(t("Required")),
  });

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      targetCompletionDate: new Date(),
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const vars = {
        variables: {
          orgId: store.currentOrg.org.id,
          templateId: store.currentTestTemplate.id,
          instanceOptions: {
            targetCompletionDate: values.targetCompletionDate,
          },
          assignee: {
            firstname: values.firstName,
            lastname: values.lastName,
            email: values.email,
          },
          tagIds: tags.map((tag) => tag.id)
        },
      };

      // Do something with the API
      assignTestInstance(vars)
        .then((res) => {
          if (res.errors) {
            lsEnqueueSnackbar(res.errors[0].message, {
              variant: "error",
            });
            setSubmitting(false);
          } else {
            lsEnqueueSnackbar(t("TestAssigned"), {
              variant: "success",
            });
            setSubmitting(false);
            navigate("/app");
          }
        })
        .catch((e) => {
          lsEnqueueSnackbar(t("ErrorWithAssignmentTime"), {
            variant: "error",
          });
          setSubmitting(false);
          setOpenConfirmationDialog(false);
        }); // catch network errors
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={2} paddingLeft="20%" paddingRight="20%">
        <Grid item xs={12}>
          <Typography
            component="h1"
            variant="h5"
            sx={{ marginBottom: 3, fontWeight: "bold" }}
          >
            {t("AssignCandidate")}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography component="p" sx={{ marginBottom: 3 }}>
            {t("AssignCandidateExplanation")}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            name="firstName"
            variant="outlined"
            fullWidth
            id="firstName"
            label={t("FirstName")}
            value={formik.values.firstName}
            onChange={formik.handleChange}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            name="lastName"
            variant="outlined"
            fullWidth
            id="lastName"
            label={t("LastName")}
            value={formik.values.lastName}
            onChange={formik.handleChange}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            name="email"
            variant="outlined"
            fullWidth
            id="email"
            label={t("EmailAddress")}
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </Grid>
        <Grid item xs={12}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              id="targetCompletionDate"
              name="targetCompletionDate"
              label={t("TargetCompletionDate")}
              value={formik.values.targetCompletionDate}
              onChange={(value) =>
                formik.setFieldValue("targetCompletionDate", value)
              }
              renderInput={(params) => <TextField fullWidth {...params} />}
              inputVariant="outlined"
              error={
                formik.touched.targetCompletionDate &&
                Boolean(formik.errors.targetCompletionDate)
              }
              helperText={
                formik.touched.targetCompletionDate &&
                formik.errors.targetCompletionDate
              }
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12}>
          <TagControl
            tags={tags}
            setTags={setTags}
            onApplyTag={() => { return; }}
            onRemoveTag={() => { return; }}
            startInEditMode={false}
          />
        </Grid>
        <Grid container justifyContent="flex-end">
          <Grid item sx={{ textAlign: "right" }}>
            <Button
              onClick={() => {
                handleOpenDialog();
              }}
              variant="contained"
              color="primary"
              sx={{ marginTop: 3 }}
            >
              {t("Confirm")}
            </Button>
          </Grid>
        </Grid>
        <Dialog
          open={openConfirmationDialog}
          onClose={handleCloseDialog}
          aria-labelledby="form-dialog-title"
        >
          <DialogTitle id="form-dialog-title">
            {t("AssignCandidateConfirmation")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("AssignCandidateConfirmationExplanation")}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDialog}
              color="secondary"
              variant="contained"
            >
              {t("Cancel")}
            </Button>
            <Button
              onClick={formik.handleSubmit}
              color="primary"
              variant="contained"
            >
              {t("Confirm")}
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </form>
  );
};

export default AssignCandidateForm;
